/**
 * QuestionFocusMode
 *
 * A modal component for focused quiz question review in messages
 *
 * Primary responsibilities:
 * - Display quiz questions in a full-screen modal view with darkened background
 * - Provide navigation between multiple questions with next/prev buttons
 * - Track and display score for correct/incorrect answers
 * - Support keyboard navigation
 * - Display detailed explanations for answers
 * - Render question/options/explanations as markdown with Mermaid and images
 */

import React, { useState, useEffect, useCallback } from 'react'
import { Dialog, DialogContent, DialogOverlay } from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import {
  IconArrowLeft,
  IconArrowRight,
  IconCheck,
  IconX,
  IconQuestionMark,
  IconBulb,
  IconRefresh
} from '@tabler/icons-react'
import { cn } from '@/lib/utils'
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group'
import { Label } from '@/components/ui/label'
import { Streamdown, type Components } from 'streamdown'
import { MermaidComponent } from '@/components/messages/message-markdown'
import { streamdownStaticProps } from '@/components/messages/streamdown-animation'
import { streamdownMathPlugins } from '@/components/messages/streamdown-math'

export interface QuestionItem {
  question: string
  options: string[]
  correctAnswer: string // The letter (A, B, C, D) of the correct answer
  explanation: string
  conceptExplanation?: string
  categoryTag?: string
  difficultyLevel?: 'easy' | 'medium' | 'hard'
}

interface QuestionFocusModeProps {
  questions: QuestionItem[]
  isOpen: boolean
  onOpenChange: (isOpen: boolean) => void
}

// Custom DialogOverlay with enhanced blur effect
const BlurredDialogOverlay = React.forwardRef<
  React.ElementRef<typeof DialogOverlay>,
  React.ComponentPropsWithoutRef<typeof DialogOverlay>
>(({ className, ...props }, ref) => (
  <DialogOverlay
    ref={ref}
    className={cn(
      'bg-background/40 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 fixed inset-0 z-50 grid overflow-auto p-4 backdrop-blur-xl sm:place-items-center',
      className
    )}
    {...props}
  />
))
BlurredDialogOverlay.displayName = 'BlurredDialogOverlay'

const quizMarkdownComponents: Components = {
  pre({ children, className: preClassName, ...props }) {
    const codeChildren = React.Children.toArray(children).filter(
      (child): child is React.ReactElement<{
        children?: React.ReactNode
        className?: string
      }> => React.isValidElement(child) && 'children' in child.props
    )
    const childClassName = codeChildren
      .map((child) => child.props.className)
      .find((className): className is string => typeof className === 'string')
    const match = /language-(\w+)/.exec(
      [preClassName, childClassName].filter(Boolean).join(' ')
    )
    const language = match ? match[1].toLowerCase() : ''
    const content = codeChildren
      .map((child) =>
        typeof child.props.children === 'string'
          ? child.props.children
          : Array.isArray(child.props.children)
            ? child.props.children.join('')
            : ''
      )
      .join('')
    const isMermaidContent = content
      .trim()
      .match(
        /^(graph|flowchart|sequenceDiagram|classDiagram|stateDiagram|erDiagram|journey|gantt|pie|mindmap|timeline|gitGraph)\s/i
      )
    const isMermaidFence = content.trim().toLowerCase().startsWith('```mermaid')
    const isMermaid =
      language === 'mermaid' || !!isMermaidContent || isMermaidFence

    if (isMermaid) {
      const cleanContent = isMermaidFence
        ? content.replace(/^```mermaid\n?/, '').replace(/```$/, '').trim()
        : content.trim()
      return (
        <div className="my-3">
          <MermaidComponent content={cleanContent} isStreaming={false} />
        </div>
      )
    }
    return (
      <pre className={cn('bg-muted/50 overflow-x-auto rounded-lg p-3', preClassName)} {...props}>
        {children}
      </pre>
    )
  },
  code({ className: codeClassName, children, ...props }) {
    const match = /language-(\w+)/.exec(codeClassName || '')
    const language = match ? match[1].toLowerCase() : ''
    const codeContent = children?.toString() || ''
    const isMermaidContent = codeContent
      .trim()
      .match(
        /^(graph|flowchart|sequenceDiagram|classDiagram|stateDiagram|erDiagram|journey|gantt|pie|mindmap|timeline|gitGraph)\s/i
      )
    const isMermaidFence = codeContent.trim().toLowerCase().startsWith('```mermaid')
    const isMermaid =
      language === 'mermaid' || !!isMermaidContent || isMermaidFence
    const isCodeBlock = language.length > 0 || 'data-block' in props
    if (isCodeBlock && isMermaid) {
      return null
    }
    return (
      <code
        className={cn(
          'bg-muted/70 rounded px-1.5 py-0.5 font-mono text-sm',
          codeClassName
        )}
        {...props}
      >
        {children}
      </code>
    )
  },
  img({ src, alt, ...imgProps }) {
    return (
      <span className="border-border bg-muted/30 my-2 block overflow-hidden rounded-lg border">
        {/* eslint-disable-next-line @next/next/no-img-element */}
        <img
          src={src || ''}
          alt={alt || ''}
          className="max-h-80 w-full object-contain"
          {...imgProps}
        />
      </span>
    )
  }
}

// Renders markdown with Mermaid diagrams and images for quiz question/options/explanations
function QuizContentMarkdown({
  content,
  className,
  asParagraph
}: {
  content: string
  className?: string
  /** When true, wrap in a single paragraph-style container (e.g. for option text) */
  asParagraph?: boolean
}) {
  if (!content?.trim()) return null

  const wrapperClass = cn(
    'prose prose-sm dark:prose-invert max-w-none [&_*]:text-inherit',
    asParagraph && 'inline [&_p]:my-0 [&_p]:inline',
    className
  )

  return (
    <div className={wrapperClass}>
      <Streamdown
        {...streamdownStaticProps}
        className={asParagraph ? 'inline space-y-0' : 'space-y-0'}
        plugins={streamdownMathPlugins}
        components={quizMarkdownComponents}
      >
        {content}
      </Streamdown>
    </div>
  )
}

export const QuestionFocusMode: React.FC<QuestionFocusModeProps> = ({
  questions,
  isOpen,
  onOpenChange
}) => {
  const [currentIndex, setCurrentIndex] = useState(0)
  const [scoreCorrect, setScoreCorrect] = useState(0)
  const [scoreIncorrect, setScoreIncorrect] = useState(0)
  const [reviewedQuestions, setReviewedQuestions] = useState<Set<number>>(new Set())
  const [selectedAnswers, setSelectedAnswers] = useState<Record<number, string>>({})
  const [showExplanations, setShowExplanations] = useState<Record<number, boolean>>({})
  const [showConceptHelp, setShowConceptHelp] = useState<Record<number, boolean>>({})

  // Reset state when modal opens
  useEffect(() => {
    if (isOpen) {
      setCurrentIndex(0)
      setScoreCorrect(0)
      setScoreIncorrect(0)
      setReviewedQuestions(new Set())
      setSelectedAnswers({})
      setShowExplanations({})
      setShowConceptHelp({})
    }
  }, [isOpen])

  const handlePrevious = useCallback(() => {
    // Navigate to previous question
    setCurrentIndex((prev) => Math.max(0, prev - 1))
  }, [])

  const handleNext = useCallback(() => {
    // Navigate to next question
    setCurrentIndex((prev) => Math.min(questions.length - 1, prev + 1))
  }, [questions.length])

  const handleOptionSelect = useCallback(
    (value: string) => {
      if (selectedAnswers[currentIndex]) return // Don't allow changing answer

      // Set the selected answer
      setSelectedAnswers((prev) => ({
        ...prev,
        [currentIndex]: value
      }))

      // Show explanation
      setShowExplanations((prev) => ({
        ...prev,
        [currentIndex]: true
      }))

      // Mark as reviewed
      if (!reviewedQuestions.has(currentIndex)) {
        setReviewedQuestions((prev) => new Set(prev).add(currentIndex))

        // Update score
        const isCorrect = value === questions[currentIndex].correctAnswer
        if (isCorrect) {
          setScoreCorrect((prev) => prev + 1)
        } else {
          setScoreIncorrect((prev) => prev + 1)
        }
      }
    },
    [currentIndex, questions, reviewedQuestions, selectedAnswers]
  )

  const toggleConceptHelp = useCallback(() => {
    setShowConceptHelp((prev) => ({
      ...prev,
      [currentIndex]: !prev[currentIndex]
    }))
  }, [currentIndex])

  // Keyboard navigation
  useEffect(() => {
    if (!isOpen) return

    const handleKeyDown = (e: KeyboardEvent) => {
      switch (e.key) {
        case 'ArrowLeft':
          handlePrevious()
          break
        case 'ArrowRight':
          handleNext()
          break
        case 'Escape':
          onOpenChange(false)
          break
        case '1':
        case '2':
        case '3':
        case '4':
          // Map key 1-4 to options A-D
          const optionIndex = parseInt(e.key) - 1
          if (optionIndex >= 0 && optionIndex < questions[currentIndex].options.length) {
            const letter = String.fromCharCode(65 + optionIndex) // 65 is ASCII for 'A'
            handleOptionSelect(letter)
          }
          break
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [
    isOpen,
    handlePrevious,
    handleNext,
    onOpenChange,
    handleOptionSelect,
    currentIndex,
    questions
  ])

  // Add a function to reset/restart the session
  const handleRestart = useCallback(() => {
    setCurrentIndex(0)
    setScoreCorrect(0)
    setScoreIncorrect(0)
    setReviewedQuestions(new Set())
    setSelectedAnswers({})
    setShowExplanations({})
    setShowConceptHelp({})
  }, [])

  if (!questions.length) return null

  const currentQuestion = questions[currentIndex]
  const totalReviewed = scoreCorrect + scoreIncorrect
  const progress = totalReviewed > 0 ? Math.round((scoreCorrect / totalReviewed) * 100) : 0
  const selectedOption = selectedAnswers[currentIndex] || null
  const showExplanation = showExplanations[currentIndex] || false
  const isCorrect = selectedOption === currentQuestion.correctAnswer
  const isIncorrect = selectedOption !== null && selectedOption !== currentQuestion.correctAnswer

  // Create option letters (A, B, C, D, etc.)
  const optionLetters = currentQuestion.options.map(
    (_, index) => String.fromCharCode(65 + index) // 65 is ASCII for 'A'
  )

  // Get the difficulty display text
  const difficultyDisplay = {
    easy: 'Easy',
    medium: 'Medium',
    hard: 'Advanced'
  }

  // Split explanation into basic and concept parts if not provided separately
  const hasExplicitConceptExplanation = !!currentQuestion.conceptExplanation
  const parsedExplanation =
    !hasExplicitConceptExplanation && currentQuestion.explanation
      ? currentQuestion.explanation.split('[UNDERSTAND]').map((part) => part.trim())
      : [currentQuestion.explanation, currentQuestion.conceptExplanation || '']

  const basicExplanation = parsedExplanation[0]
  const derivedConceptExplanation = parsedExplanation.length > 1 ? parsedExplanation[1] : ''
  const finalConceptExplanation = hasExplicitConceptExplanation
    ? currentQuestion.conceptExplanation
    : derivedConceptExplanation

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <BlurredDialogOverlay />
      <DialogContent
        className="bg-background/95 flex h-[85vh] max-w-4xl flex-col overflow-hidden border-none p-0 shadow-xl backdrop-blur-2xl"
        forceMount
      >
        {/* Fixed position buttons in top-right corner */}
        <div className="absolute right-4 top-4 z-10 flex gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={handleRestart}
            className="border-border/50 bg-background/80 hover:bg-background/90 flex h-8 items-center gap-1 rounded-full border backdrop-blur-sm"
          >
            <IconRefresh size={14} />
            <span>Try Again</span>
          </Button>
          <Button
            variant="outline"
            size="icon"
            onClick={() => onOpenChange(false)}
            className="border-border/50 bg-background/80 hover:bg-background/90 size-8 rounded-full border backdrop-blur-sm"
          >
            <IconX size={16} />
            <span className="sr-only">Exit</span>
          </Button>
        </div>

        <div className="flex items-center justify-between border-b p-4">
          <div className="flex items-center gap-2">
            <IconQuestionMark size={18} className="text-muted-foreground" />
            <h2 className="text-lg font-semibold">Question Review</h2>

            {currentQuestion.categoryTag && (
              <span className="ml-1 rounded-full bg-blue-100 px-2 py-0.5 text-xs font-medium text-blue-800 dark:bg-blue-900/20 dark:text-blue-300">
                {currentQuestion.categoryTag}
              </span>
            )}

            {currentQuestion.difficultyLevel && (
              <span
                className={cn(
                  'ml-1 rounded-full px-2 py-0.5 text-xs font-medium',
                  currentQuestion.difficultyLevel === 'easy'
                    ? 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-300'
                    : currentQuestion.difficultyLevel === 'medium'
                      ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-300'
                      : 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-300'
                )}
              >
                {difficultyDisplay[currentQuestion.difficultyLevel]}
              </span>
            )}
          </div>

          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <span className="flex items-center font-medium text-green-600 dark:text-green-400">
                <IconCheck size={16} className="mr-1" />
                {scoreCorrect}
              </span>
              <span className="flex items-center font-medium text-red-600 dark:text-red-400">
                <IconX size={16} className="mr-1" />
                {scoreIncorrect}
              </span>
            </div>

            <div className="flex items-center gap-1">
              <span className="text-sm">
                {currentIndex + 1}/{questions.length}
              </span>
              <div className="h-2 w-24 overflow-hidden rounded-full bg-gray-200 dark:bg-gray-700">
                <div
                  className="h-full bg-blue-500 transition-all duration-300"
                  style={{ width: `${((currentIndex + 1) / questions.length) * 100}%` }}
                />
              </div>
            </div>

            <div className="text-sm font-medium">Score: {progress}%</div>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-6">
          <div className="mx-auto max-w-3xl">
            <div className="flex flex-col gap-4">
              <div className="text-lg font-medium">
                <QuizContentMarkdown content={currentQuestion.question} />
              </div>

              <RadioGroup
                value={selectedOption || ''}
                onValueChange={handleOptionSelect}
                className="space-y-2"
              >
                {currentQuestion.options.map((option, index) => {
                  const letter = optionLetters[index]
                  return (
                    <div
                      key={index}
                      className={cn(
                        'flex items-start space-x-2 rounded-md p-2 transition-colors',
                        selectedOption === letter &&
                          letter === currentQuestion.correctAnswer &&
                          'bg-green-100 dark:bg-green-900/20',
                        selectedOption === letter &&
                          letter !== currentQuestion.correctAnswer &&
                          'bg-red-100 dark:bg-red-900/20',
                        selectedOption !== null &&
                          letter === currentQuestion.correctAnswer &&
                          'bg-green-50 dark:bg-green-900/10'
                      )}
                    >
                      <RadioGroupItem
                        value={letter}
                        id={`option-${letter}`}
                        disabled={selectedOption !== null}
                        className="mt-0.5"
                      />
                      <Label
                        htmlFor={`option-${letter}`}
                        className={cn(
                          'flex-1 cursor-pointer text-sm leading-relaxed',
                          selectedOption !== null && 'cursor-default'
                        )}
                      >
                        <span className="mr-2 font-medium">{letter}.</span>
                        <QuizContentMarkdown content={option} asParagraph className="inline" />
                        {selectedOption !== null && letter === currentQuestion.correctAnswer && (
                          <IconCheck className="ml-2 inline size-4 text-green-600 dark:text-green-400" />
                        )}
                      </Label>
                    </div>
                  )
                })}
              </RadioGroup>

              {showExplanation && (
                <div className="mt-4 space-y-4">
                  <div
                    className={cn(
                      'rounded-lg p-4 text-sm',
                      isCorrect
                        ? 'border border-green-100 bg-green-50 dark:border-green-800/20 dark:bg-green-900/10'
                        : 'border border-red-100 bg-red-50 dark:border-red-800/20 dark:bg-red-900/10'
                    )}
                  >
                    <h4 className="mb-2 font-medium">{isCorrect ? 'Correct! ✓' : 'Incorrect ✗'}</h4>
                    <QuizContentMarkdown content={basicExplanation} />
                  </div>

                  {finalConceptExplanation && (
                    <div>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={toggleConceptHelp}
                        className="mb-2"
                      >
                        <IconBulb size={16} className="mr-2" />
                        {showConceptHelp[currentIndex]
                          ? 'Hide Deeper Explanation'
                          : 'Help Me Understand'}
                      </Button>

                      {showConceptHelp[currentIndex] && (
                        <div className="rounded-lg border border-blue-100 bg-blue-50 p-4 text-sm dark:border-blue-800/20 dark:bg-blue-900/10">
                          <h4 className="mb-2 font-medium">Concept Explanation</h4>
                          <QuizContentMarkdown content={finalConceptExplanation} />
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="flex items-center justify-between border-t p-4">
          <Button variant="outline" onClick={handlePrevious} disabled={currentIndex === 0}>
            <IconArrowLeft size={16} className="mr-2" />
            Previous
          </Button>

          <div className="text-muted-foreground text-sm">
            {!selectedOption
              ? 'Select an answer to see the explanation'
              : 'Use arrow keys to navigate between questions'}
          </div>

          <Button
            variant="outline"
            onClick={handleNext}
            disabled={currentIndex === questions.length - 1}
          >
            Next
            <IconArrowRight size={16} className="ml-2" />
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  )
}
